import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../lib/session'
import { loadMilestoneSettings, setTriggerEnabled } from '../lib/milestonesettings'
import { LABEL, TEMPLATE_NAME, TRIGGER_TAG, WHEN, type Trigger } from '../lib/milestones'
import type { MilestoneSetting } from '../lib/types'

/**
 * The eight real triggers, in the order `milestones.ts` itself declares
 * them (`SHAPE`'s key order) — the same order `buildMilestoneTemplates`
 * walks, so this list and the Templates screen's milestone rows read the
 * same way. Written out rather than derived from `Object.keys(SHAPE)`: a
 * `Record<Trigger, …>`'s key order is an implementation detail of object
 * literals, not a contract, and this screen is exactly the kind of place
 * where a silent reorder would go unnoticed for months.
 */
const TRIGGERS: readonly Trigger[] = [
  'birthday',
  'anniversary',
  'capped',
  'elite',
  'first_closing',
  'welcome',
  'departed',
  'sc_licensed',
]

/**
 * Fields that could become a ninth trigger, and are not — flagged 2026-08-23
 * (claude/milestone-triggers-tab-flagged-2026-08-23.md), Chris's own "not
 * sure what else posts to Metabase" question answered on screen rather than
 * left open. Column names below are the real Metabase card columns and the
 * `agents` table columns they land in, confirmed against
 * `claude/metabase-card-shapes.md` — not guesses.
 *
 * Static, not read from anywhere — there is no data behind "candidate", only
 * the fact that a real column already exists to build one from. Adding an
 * ninth trigger for real means writing it into `milestones.ts` the way the
 * other eight are, exactly as the design doc says: by request, same as
 * today, not through a self-service builder (deliberately not built — see
 * the design doc's "explicitly not building now" section).
 */
const CANDIDATES: readonly { title: string; detail: string }[] = [
  {
    title: 'A second, fifth, or tenth closing',
    detail:
      'Metabase already reports closed-transaction counts ("Closed Transactions" on the card, ' +
      'closed_transactions on the agent record) — first_closing exists today and reuses this ' +
      'same shape, a later closing does not yet have a trigger of its own.',
  },
  {
    title: 'A first listing taken',
    detail:
      'Also already in the feed and unused by any trigger today: "Active Listings" on the ' +
      'card, active_listings on the agent record. The natural analogue to first_closing, for ' +
      'listings instead of closings.',
  },
  {
    title: 'A reZEN-sourced change — a mailing address, for instance',
    detail:
      "Not from Metabase at all, but the signal already exists: roster_changes already logs " +
      "field-level changes from the weekly reZEN sync (kind = 'field_updated'). No new sync " +
      'work would be needed to feed a trigger from it — but reZEN only syncs weekly and needs ' +
      'a signed-in browser to run, so this would always be up to a week slower to notice than ' +
      'the Metabase-driven triggers above, and whether an address change is worth emailing an ' +
      'agent about at all is a real product call, not assumed here.',
  },
]

/**
 * The Triggers tab, flagged 2026-08-23 during the walkthrough after the
 * Scheduled Sends history questions. Two parts, per the design doc:
 *
 * 1. A read-only reference list of the eight real triggers — what fires
 *    each one, which template it drafts from, which tag turns it off for
 *    one agent. Surfaces `TEMPLATE_NAME`/`WHEN`/`TRIGGER_TAG`/`LABEL`
 *    exactly as `milestones.ts` already has them, rather than inventing new
 *    copy that could drift from what `plan()` actually does.
 * 2. An on/off switch per trigger, backed by the new `milestone_settings`
 *    table. Flipping one off does not stop mid-flight — `plan()` declines a
 *    disabled trigger's events the same way a suppressed one is declined,
 *    claimed as baseline so re-enabling it later does not draft a backlog.
 *
 * No delete/build-your-own affordance here — deliberately. The design doc
 * gives real reasons a generic trigger builder is not being built now
 * (`isNewCycle`'s own history: 381 false congratulations during testing,
 * from getting exactly this kind of detection logic wrong once). This
 * screen documents and toggles the eight that exist; a new one is still a
 * request to Claude, same as today.
 *
 * Laid out with the same `.gaps`/`.entry` card-row convention
 * `Tasks.tsx`/`Bounces.tsx` already use, rather than a `<table>` — this app
 * has no table anywhere else, and a one-off element would need its own
 * unreviewed CSS instead of the styling every other list screen already has.
 */
export default function Triggers() {
  const { appUser } = useAuth()
  const [settings, setSettings] = useState<MilestoneSetting[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<Set<string>>(new Set())
  const [toggleError, setToggleError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const { settings, error } = await loadMilestoneSettings()
    if (error) setError(error)
    else setSettings(settings)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  // Missing a row for a trigger reads as enabled — the same default plan()
  // itself falls back to when `milestone_settings` has no row for one, so
  // this screen never disagrees with what the run actually does.
  const enabledFor = (trigger: Trigger): boolean =>
    (settings ?? []).find((s) => s.trigger === trigger)?.enabled ?? true

  async function toggle(trigger: Trigger) {
    setBusy((prev) => new Set(prev).add(trigger))
    setToggleError(null)
    try {
      const { setting, error } = await setTriggerEnabled(trigger, !enabledFor(trigger), appUser?.id ?? null)
      if (error || !setting) {
        setToggleError(`Could not change ${LABEL[trigger]}: ${error ?? 'unknown error'}`)
        return
      }
      setSettings((prev) => {
        const rest = (prev ?? []).filter((s) => s.trigger !== trigger)
        return [...rest, setting].sort((a, b) => a.trigger.localeCompare(b.trigger))
      })
    } finally {
      setBusy((prev) => {
        const next = new Set(prev)
        next.delete(trigger)
        return next
      })
    }
  }

  if (error) return <div className="empty">Could not load trigger settings: {error}</div>
  if (!settings) return <div className="empty">Loading…</div>

  return (
    <div className="gaps">
      <h2>Milestone triggers</h2>
      <p className="hint">
        The eight events that can draft a milestone email on their own, without her writing one.
        Turning one off here stops it going forward — anything it already drafted stays in Daily
        Outreach, and an event that happens while it's off is recorded but not drafted, so
        turning it back on later does not suddenly draft a backlog.
      </p>
      {toggleError && <p className="error">{toggleError}</p>}

      {TRIGGERS.map((trigger) => {
        const on = enabledFor(trigger)
        const tag = TRIGGER_TAG[trigger]
        return (
          <div className={`entry${on ? '' : ' off'}`} key={trigger}>
            <div className="emeta">
              <span className="pill s-drafted">{TEMPLATE_NAME[trigger]}</span>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                className={`toggle${on ? ' on' : ''}`}
                disabled={busy.has(trigger)}
                onClick={() => void toggle(trigger)}
                aria-label={`Turn ${LABEL[trigger]} ${on ? 'off' : 'on'}`}
              />
            </div>
            <div className="dsubject">{LABEL[trigger]}</div>
            <div className="hint">{WHEN[trigger]}</div>
            <div className="hint">
              {tag ? (
                <>
                  Suppress tag: <code>{tag}</code>
                </>
              ) : (
                "No suppress tag — this one has no per-agent opt-out."
              )}
            </div>
          </div>
        )
      })}

      <h2 style={{ marginTop: 24 }}>Candidates — not built yet</h2>
      <p className="hint">
        Fields that already exist in the data and could become a trigger, but don't fire anything
        today. Building one for real is still a request to Claude, the same as any of the eight
        above were.
      </p>
      {CANDIDATES.map((c) => (
        <div className="entry" key={c.title}>
          <div className="dsubject">{c.title}</div>
          <div className="hint">{c.detail}</div>
        </div>
      ))}
    </div>
  )
}
